import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Visitor account credentials - this account is used for guest checkout
const VISITOR_EMAIL = 'visitor@store.local';
const VISITOR_PASSWORD = 'visitor_guest_2024';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isVisitor: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isVisitor, setIsVisitor] = useState(false);
  const visitorLoginInFlight = useRef(false);
  const visitorRetryAfter = useRef(0);


  // Ensure visitor account exists + is confirmed (fixes email_not_confirmed)
  const ensureVisitorAccountReady = async () => {
    try {
      const { error } = await supabase.functions.invoke('ensure-visitor-account', {
        body: {
          email: VISITOR_EMAIL,
          password: VISITOR_PASSWORD,
          full_name: 'Guest Visitor',
        },
      });
      if (error) {
        console.warn('Visitor account preparation unavailable');
        return false;
      }
      return true;
    } catch (err) {
      // Silent fail: checkout can still work for anon policies, but we prefer visitor session
      console.warn('ensureVisitorAccountReady failed:', err);
      return false;
    }
  };

  // Ensure a profiles row exists for the signed-in user (needed for staff list name/email)
  const ensureProfileExists = async (sessionUser: User) => {
    try {
      const fullName = (sessionUser.user_metadata as any)?.full_name ?? null;
      const email = sessionUser.email ?? null;

      const { data: existing, error: fetchError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('user_id', sessionUser.id)
        .maybeSingle();

      if (fetchError) {
        console.warn('ensureProfileExists: failed to fetch profile:', fetchError);
        return;
      }

      if (!existing) {
        const { error: insertError } = await supabase.from('profiles').insert({
          user_id: sessionUser.id,
          full_name: fullName,
          email,
        });
        if (insertError) console.warn('ensureProfileExists: insert failed:', insertError);
        return;
      }

      // Backfill missing fields only
      const nextFullName = existing.full_name ?? fullName;
      const nextEmail = existing.email ?? email;
      if (nextFullName !== existing.full_name || nextEmail !== existing.email) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ full_name: nextFullName, email: nextEmail })
          .eq('id', existing.id);
        if (updateError) console.warn('ensureProfileExists: update failed:', updateError);
      }
    } catch (err) {
      console.warn('ensureProfileExists: unexpected error:', err);
    }
  };


  // Auto-login as visitor if not logged in (skip if on admin login page)
  const autoLoginAsVisitor = async () => {
    // Check if we're on admin login page - don't auto-login as visitor
    if (visitorLoginInFlight.current || Date.now() < visitorRetryAfter.current || sessionStorage.getItem('admin_login_in_progress') === 'true') {
      return;
    }

    visitorLoginInFlight.current = true;
    visitorRetryAfter.current = Date.now() + 30_000;
    try {
      // Existing visitors should sign in without listing or mutating auth users.
      const signInVisitor = async () =>
        supabase.auth.signInWithPassword({
          email: VISITOR_EMAIL,
          password: VISITOR_PASSWORD,
        });

      const { error } = await signInVisitor();

      if (error) {
        // Most common cases: email_not_confirmed or old password mismatch
        const msg = (error.message || '').toLowerCase();
        if (msg.includes('email not confirmed') || msg.includes('invalid login credentials')) {
          const ready = await ensureVisitorAccountReady();
          if (!ready) return;
          const { error: retryError } = await signInVisitor();
          if (retryError) {
            console.error('Visitor login error (after ensure):', retryError);
          }
          return;
        }

        console.error('Visitor login error:', error);
      }
    } catch (err) {
      console.error('Auto-login error:', err);
    } finally {
      visitorLoginInFlight.current = false;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Ensure profile exists for signed in users
        if (session?.user) {
          setTimeout(() => {
            ensureProfileExists(session.user);
          }, 0);
        }

        // Check if this is the visitor account
        const isVisitorAccount = session?.user?.email === VISITOR_EMAIL;
        setIsVisitor(isVisitorAccount);

        // Check admin role with setTimeout to avoid deadlock
        if (session?.user && !isVisitorAccount) {
          setTimeout(() => {
            checkAdminRole(session.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
        }

        // If no session and no loading, auto-login as visitor
        if (event === 'SIGNED_OUT' || (!session && event === 'INITIAL_SESSION')) {
          setTimeout(() => {
            autoLoginAsVisitor();
          }, 100);
        }

        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        ensureProfileExists(session.user);
      }

      const isVisitorAccount = session?.user?.email === VISITOR_EMAIL;
      setIsVisitor(isVisitorAccount);

      if (session?.user && !isVisitorAccount) {
        checkAdminRole(session.user.id);
      }

      // If no session, auto-login as visitor
      if (!session) {
        autoLoginAsVisitor();
      }

      setLoading(false);
    }).catch((error) => {
      console.warn('Session recovery unavailable:', error);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();

      if (error) {
        console.error('Error checking admin role:', error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(!!data);
    } catch (err) {
      console.error('Error checking admin role:', err);
      setIsAdmin(false);
    }
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName
        }
      }
    });

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsVisitor(false);
    // Re-login as visitor after sign out
    setTimeout(() => {
      autoLoginAsVisitor();
    }, 100);
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/admin/login?reset=true`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });

    return { error: error as Error | null };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, isVisitor, signUp, signIn, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
